/**
 * PORTADA · «MAS QUE FUTBOL»
 * =============================================================================
 * La fotografia de fondo, el titular partido en blanco y dorado, el lema, los
 * valores, el boton y la franja de cinco pilares.
 *
 * -----------------------------------------------------------------------------
 * LA FOTOGRAFIA
 * -----------------------------------------------------------------------------
 * Es horizontal, 16:9, y el nino esta en el centro-derecha. El texto va a la
 * izquierda, asi que se oscurece mas ese lado con el velo: es el mismo recurso que
 * usa la referencia y es lo que hace que el titular blanco se lea sin esfuerzo.
 *
 * -----------------------------------------------------------------------------
 * LO QUE SE CUIDO DE LA VERSION ANTERIOR
 * -----------------------------------------------------------------------------
 *   · EL BOTON. Tenia un ancho minimo y el texto se partia en dos lineas, asi que
 *     crecia y se salia de su sitio. Ahora el ancho lo pone el contenido y el texto
 *     no se parte.
 *   · LOS MARGENES. El titular, el boton y los pilares comparten contenedor, asi
 *     que quedan alineados por la izquierda.
 *   · EL TITULAR. Se ajusta solo con `clamp`, asi que en un telefono de 320 px no
 *     se sale.
 */

import { CONFIG } from '../config';
import { Icono, IconoEnAro, type NombreIcono } from '../componentes/Iconos';
import { Revelar } from '../componentes/Piezas';
import { useIdioma } from '../i18n/useIdioma';
import { useAjustes } from '../i18n/useAjustes';

export function Portada() {
  const { textos } = useIdioma();
  const { valor } = useAjustes();
  const t = textos.inicio;

  const irA = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="inicio" className="portada">
      {/* La fotografia, en su propia capa para poder encuadrarla sin tocar el
          contenido. */}
      <div className="portada__fondo" aria-hidden="true">
        <img src={CONFIG.fotos.portadaInicio} alt="" />
      </div>

      {/* El velo. Sin el, el texto blanco no se leeria sobre una fotografia clara, y
          es el mismo recurso que usa la referencia. */}
      <div className="portada__velo" aria-hidden="true" />

      <div className="caja portada__contenido">
        <div className="portada__textos">
          <Revelar>
            <p className="portada__lema">{valor('academia_lema', t.lema)}</p>
          </Revelar>

          <Revelar retardo={70}>
            <h1 className="portada__titulo">
              {t.titulo1}
              <br />
              <span className="oro">{t.titulo2}</span>
            </h1>
          </Revelar>

          <Revelar retardo={140}>
            <p className="portada__subtitulo">{t.subtitulo}</p>
          </Revelar>

          <Revelar retardo={200}>
            <p className="portada__valores">{t.valores}</p>
          </Revelar>

          <Revelar retardo={260}>
            <div className="acciones-fila">
              <button
                type="button"
                className="boton boton--oro"
                onClick={() => irA('programas')}
              >
                {/* El texto va en un `span` para que no se parta en dos lineas: es lo
                    que descuadraba el boton. */}
                <span>{t.boton}</span>
                <Icono nombre="flecha" tam={17} />
              </button>
            </div>
          </Revelar>
        </div>
      </div>

      {/* La franja de los cinco pilares, al pie de la portada. */}
      <div className="pilares">
        {textos.pilares.map((p, i) => (
          <button
            type="button"
            className="pilar"
            key={p.titulo}
            onClick={() => irA(i === textos.pilares.length - 1 ? 'competencias' : 'programas')}
          >
            <IconoEnAro nombre={p.icono as NombreIcono} tam={54} grosorIcono={22} />
            <span className="pilar__texto">{p.titulo}</span>
          </button>
        ))}
      </div>

      <div className="portada__desliza" aria-hidden="true">
        <span>{t.desliza}</span>
        <Icono nombre="desliza" tam={22} />
      </div>
    </section>
  );
}
